import { useEffect, useState } from 'react';
import { Canvas, Group } from 'fabric';
import { arrangeGroup } from './arrange';
import { readGroupCollection } from './read-group-collection';
import type { GroupCollectionConfig, GroupCollectionEquispacedConfig, GroupCollectionGridConfig } from './types';

export type Mode = 'none' | 'row' | 'column' | 'grid';

function writeGroupCollection(group: Group, cfg: GroupCollectionConfig | undefined): void {
  if (cfg) group.groupCollection = cfg;
  else delete group.groupCollection;
}

function readEquispaced(group: Group): GroupCollectionEquispacedConfig | undefined {
  const cfg = readGroupCollection(group);
  return cfg?.type === 'equispaced' ? cfg : undefined;
}

function readGrid(group: Group): GroupCollectionGridConfig | undefined {
  const cfg = readGroupCollection(group);
  return cfg?.type === 'grid' ? cfg : undefined;
}

export type GroupSettingsState = {
  mode: Mode;
  isBound: boolean;
  size: number;
  gap: number;
  gridWidth: number;
  gridHeight: number;
  rowGap: number;
  colGap: number;
  maxItemHeight: number | undefined;
  commit: () => void;
  onMode: (next: Mode) => void;
  onSize: (v: number) => void;
  onGap: (v: number) => void;
  onGridWidth: (v: number) => void;
  onGridHeight: (v: number) => void;
  onRowGap: (v: number) => void;
  onColGap: (v: number) => void;
  onToggleMaxItemHeight: (enabled: boolean) => void;
  onMaxItemHeight: (v: number) => void;
};

export function useGroupSettings(canvas: Canvas | null, group: Group | null): GroupSettingsState {
  const initialEquispaced = group ? readEquispaced(group) : undefined;
  const initialGrid = group ? readGrid(group) : undefined;
  const initialMode: Mode = initialGrid ? 'grid' : (initialEquispaced?.direction ?? 'none');

  const [mode, setMode] = useState<Mode>(initialMode);
  const [size, setSize] = useState(initialEquispaced?.size ?? 200);
  const [gap, setGap] = useState(initialEquispaced?.gap ?? 0);
  const [gridWidth, setGridWidth] = useState(initialGrid?.width ?? 400);
  const [gridHeight, setGridHeight] = useState(initialGrid?.height ?? 400);
  const [rowGap, setRowGap] = useState(initialGrid?.rowGap ?? 16);
  const [colGap, setColGap] = useState(initialGrid?.colGap ?? 16);
  const [maxItemHeight, setMaxItemHeight] = useState<number | undefined>(initialGrid?.maxItemHeight);

  useEffect(() => {
    if (!group) return;
    const eq = readEquispaced(group);
    const grid = readGrid(group);
    if (grid) {
      setMode('grid');
      setGridWidth(grid.width);
      setGridHeight(grid.height);
      setRowGap(grid.rowGap ?? 16);
      setColGap(grid.colGap ?? 16);
      setMaxItemHeight(grid.maxItemHeight);
    } else if (eq) {
      setMode(eq.direction);
      setSize(eq.size);
      setGap(eq.gap ?? 0);
    } else {
      setMode('none');
    }
  }, [group]);

  const isBound = !!(group && readGroupCollection(group)?.urlPath);

  /**
   * Captured, not dispatched: arrangeGroup recomputes the children (positions, fit scales) in
   * Fabric, and a data-bound collection's children only exist there. Firing object:modified lets
   * the reverse sync read the arranged result into the model, config and children together.
   */
  const commit = (): void => {
    if (!canvas || !group) return;
    canvas.fire('object:modified', { target: group });
  };

  const applyEquispaced = (direction: 'row' | 'column', nextSize: number, nextGap: number): void => {
    if (!canvas || !group) return;
    const current = readEquispaced(group);
    writeGroupCollection(group, {
      ...current,
      type: 'equispaced',
      direction,
      size: nextSize,
      gap: nextGap
    });
    arrangeGroup(group);
    canvas.requestRenderAll();
  };

  const applyGrid = (
    width: number,
    height: number,
    nextRowGap: number,
    nextColGap: number,
    nextMaxItemHeight: number | undefined
  ): void => {
    if (!canvas || !group) return;
    const current = readGrid(group);
    const next: GroupCollectionGridConfig = {
      ...current,
      type: 'grid',
      width,
      height,
      rowGap: nextRowGap,
      colGap: nextColGap
    };
    if (nextMaxItemHeight !== undefined) next.maxItemHeight = nextMaxItemHeight;
    else delete next.maxItemHeight;
    writeGroupCollection(group, next);
    arrangeGroup(group);
    canvas.requestRenderAll();
  };

  const onMode = (next: Mode): void => {
    if (!canvas || !group) return;

    if (next === 'none') {
      setMode('none');
      writeGroupCollection(group, undefined);
      canvas.requestRenderAll();
      commit();
      return;
    }

    if (next === 'row' || next === 'column') {
      const existing = readEquispaced(group);
      const nextSize = existing?.size ?? (mode === 'grid' ? (next === 'column' ? gridHeight : gridWidth) : size);
      const nextGap = existing?.gap ?? (mode === 'grid' ? colGap : gap);
      setMode(next);
      setSize(nextSize);
      setGap(nextGap);
      applyEquispaced(next, nextSize, nextGap);
      commit();
      return;
    }

    const existing = readGrid(group);
    const nextWidth = existing?.width ?? (mode !== 'none' ? size : 400);
    const nextHeight = existing?.height ?? (mode !== 'none' ? (group.height ?? 400) : 400);
    const nextRowGap = existing?.rowGap ?? (mode !== 'none' ? 0 : 16);
    const nextColGap = existing?.colGap ?? (mode !== 'none' ? 0 : 16);
    const nextMaxItemHeight = existing?.maxItemHeight;
    setMode('grid');
    setGridWidth(nextWidth);
    setGridHeight(nextHeight);
    setRowGap(nextRowGap);
    setColGap(nextColGap);
    setMaxItemHeight(nextMaxItemHeight);
    applyGrid(nextWidth, nextHeight, nextRowGap, nextColGap, nextMaxItemHeight);
    commit();
  };

  const onSize = (v: number): void => {
    setSize(v);
    applyEquispaced(mode === 'column' ? 'column' : 'row', v, gap);
  };

  const onGap = (v: number): void => {
    setGap(v);
    applyEquispaced(mode === 'column' ? 'column' : 'row', size, v);
  };

  const onGridWidth = (v: number): void => {
    setGridWidth(v);
    applyGrid(v, gridHeight, rowGap, colGap, maxItemHeight);
  };

  const onGridHeight = (v: number): void => {
    setGridHeight(v);
    applyGrid(gridWidth, v, rowGap, colGap, maxItemHeight);
  };

  const onRowGap = (v: number): void => {
    setRowGap(v);
    applyGrid(gridWidth, gridHeight, v, colGap, maxItemHeight);
  };

  const onColGap = (v: number): void => {
    setColGap(v);
    applyGrid(gridWidth, gridHeight, rowGap, v, maxItemHeight);
  };

  // Defaults to the full row height so enabling the cap doesn't visibly shrink items until dragged.
  const onToggleMaxItemHeight = (enabled: boolean): void => {
    const next = enabled ? (maxItemHeight ?? gridHeight) : undefined;
    setMaxItemHeight(next);
    applyGrid(gridWidth, gridHeight, rowGap, colGap, next);
    commit();
  };

  const onMaxItemHeight = (v: number): void => {
    setMaxItemHeight(v);
    applyGrid(gridWidth, gridHeight, rowGap, colGap, v);
  };

  return {
    mode,
    isBound,
    size,
    gap,
    gridWidth,
    gridHeight,
    rowGap,
    colGap,
    maxItemHeight,
    commit,
    onMode,
    onSize,
    onGap,
    onGridWidth,
    onGridHeight,
    onRowGap,
    onColGap,
    onToggleMaxItemHeight,
    onMaxItemHeight
  };
}
